import { Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import * as get from 'lodash/get';

import { FactOperatorBuilder } from '../fact-operators/fact-operator.builder';
import { FactMappingInterface } from '../models';
import { ConditionData } from '../models/condition-data.model';
import { OutputResult } from '../models/output-result.model';
import { OperatorBuilder } from '../operators';
import { OutputSelectorBuilder } from '../output-selector/output-selector.builder';
import { CompositeRule, Condition, ConditionTypeBuilder, FactGeneratorHelper, Rule } from '../rules';
import { ReplacerRule } from '../rules/replacer-rule';
import { Replacer } from './replacer';

@Injectable({
	providedIn: 'root'
})
export class RulesEngine {
	constructor(private factGeneratorHelper: FactGeneratorHelper) { }
	/**
	 * URL Reference TeamSite: //tstccms001/default/main/BlackRock/WebTools/WORKAREA/shared/config/data_store/bfa/areas-to-explore
	 * run: entry point for the rules engine.
	 * @param {RulesConfiguration} ruleData the configured rules (as JSON Object).
	 * @param {portfolioData} the portfolio data object.
	 * @param {defaultValue} the default value to use if the portfolio data object has any missing facts
	 */
	public run(ruleData: any, portfolioData: any, defaultValue?: any): OutputResult[] {
		const rules = this.parseConfigurationData(ruleData);
		return rules.map(rule => (<CompositeRule>rule).evaluate(portfolioData, defaultValue)).filter(x => x);
	}

	/**
	 * parseConfigurationData: runs the rules to parse the configured data.
	 * @param {Array<ConditionData>} ruleData the rules data to be parsed.
	 * @returns {Rule[]} an array of strongly typed rules.
	 */
	private parseConfigurationData(ruleData): Rule[] {
		return ruleData.map(rule => this.parseRule(rule)).filter(rule => rule);
	}

	/**
	 * parseRule: converts the raw JSON rule ito strongly typed CompositeRule object.
	 * @param {ConditionData} conditionData the rules data to be parsed.
	 * @returns {CompositeRule} strongly typed CompositeRule object.
	 */
	private parseRule(conditionData: ConditionData): Rule {
		try {
			const compositeRule: CompositeRule = new CompositeRule();
			compositeRule.type = ConditionTypeBuilder.build(conditionData.conditionType);
			compositeRule.output = new OutputResult(conditionData.output);
			compositeRule.rules = conditionData.conditions?.map(condition => {
				return new Condition(condition.fact, OperatorBuilder.build(condition.operator), condition.target, FactOperatorBuilder.build(condition.factOperator));
			});
			if (conditionData.replacer) {
				compositeRule.replacer = this.parseReplacer(conditionData.replacer);
			}
			return compositeRule;
		} catch (e) {
			console.warn('Please check config file, Rule parsing error with stack:', e);
			return null;
		}
	}

	/**
	 * parseReplacer: converts the raw replacerData to a strongly typed Replacer object with mapped rules.
	 * @param {any} replacerData the replacer data to be parsed.
	 * @returns {Replacer} strongly typed representation of the raw JSON data.
	 */
	private parseReplacer(replacerData: any): Replacer {
		const replacerMap: { [key: string]: ReplacerRule } = {};
		Object.keys(replacerData).forEach(tag => {
			const replacerRule = replacerData[tag];
			replacerMap[tag] = new ReplacerRule(OutputSelectorBuilder.build(get(replacerRule, 'outputSelector', 'selectFirst')));
			replacerMap[tag].rules = replacerRule.rules.map(rule => this.parseRule(rule));
		});

		return new Replacer(replacerMap);
	}

	/**
	 * Update the store data to run in rules engine with custom data point, new data point added can be used by calling:
	 * - '{prefix}' as store name
	 * - '{factName}' as data point name
	 * - eg. rules.fact1
	 * @param storeCombined custom store combined together in the format of [{storeName:portfolio1Data},{storeName:portfolio2Data}]
	 * @param contentSvc contentService Instance
	 * @param factMappingConfig config to be inject, all the facts are available in facts sub folder
	 * @param prefix prefix to generate as temporary store, default to be 'rules'.
	 */
	public updateStoreWithCustomDataPoints(
		storeCombined: Array<{ [storeName: string]: { [propName: string]: any } }>,
		contentSvc: ContentService,
		factMappingConfig: { [factName: string]: FactMappingInterface },
		prefix: string = 'rules'): void {
		storeCombined.forEach((portfolioStoreData) => {
			if (portfolioStoreData) {
				const customDataObject = {};
				for (const property in factMappingConfig) {
					if (factMappingConfig.hasOwnProperty(property)) {
						customDataObject[property] = this.factGeneratorHelper.extractValue(property, portfolioStoreData, factMappingConfig, contentSvc);
					}
				}
				portfolioStoreData[prefix] = customDataObject;
			}
		});
	}
}
